import { components } from "react-select";
import {
  InvoiceSourceType,
  InvoiceSourceTypeLabelMap,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import type {
  Invoice,
  InvoiceSelectOption,
} from "features/Invoices/types/invoice.types";
import { formatCurrency } from "utils/currency";

function sourceTypeLabel(sourceType: string | undefined): string | undefined {
  if (!sourceType) return undefined;
  return (
    InvoiceSourceTypeLabelMap[sourceType as InvoiceSourceType] ?? sourceType
  );
}

function sourceSummaryLine(data: InvoiceSelectOption): string | undefined {
  const typeLbl = sourceTypeLabel(data.sourceType);
  const code = data.sourceCode?.trim();
  if (typeLbl && code) return `${typeLbl} · ${code}`;
  if (typeLbl) return typeLbl;
  if (code) return code;
  return undefined;
}

export function sourceCodeFromInvoice(invoice: Invoice): string | undefined {
  const s = invoice.source;
  if (s && typeof s === "object" && "code" in s && s.code) {
    return String(s.code);
  }
  return undefined;
}

export function enrichInvoiceSelectOptionData(
  data: InvoiceSelectOption,
  invoice: Invoice | null,
): InvoiceSelectOption {
  if (!invoice || Number(data.value) !== invoice.id) {
    return data;
  }
  return {
    ...data,
    outstandingAmount:
      data.outstandingAmount ?? invoice.outstandingAmount ?? undefined,
    sourceType: data.sourceType ?? invoice.sourceType,
    sourceCode: data.sourceCode ?? sourceCodeFromInvoice(invoice),
  };
}

function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return null;
  const entry = InvoiceStatusMap[status as InvoiceStatus];
  if (!entry) return null;
  return (
    <span className={`badge bg-${entry.color}-subtle text-${entry.color} ms-1`}>
      {entry.label}
    </span>
  );
}

export function InvoiceOption(props: any) {
  const data = props.data as InvoiceSelectOption;
  const sourceLine = sourceSummaryLine(data);

  const due =
    data.amountDue != null && Number.isFinite(data.amountDue)
      ? data.amountDue
      : data.outstandingAmount != null && Number.isFinite(data.outstandingAmount)
        ? data.outstandingAmount
        : null;

  const total =
    data.total != null && Number.isFinite(data.total) ? data.total : null;

  return (
    <components.Option {...props}>
      <div className="d-flex flex-column py-1">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <span className="fw-medium text-body">{data.label}</span>
          <StatusBadge status={data.status} />
        </div>
        {sourceLine ? (
          <small className="text-muted text-truncate">{sourceLine}</small>
        ) : null}
        {due !== null || total !== null ? (
          <div className="d-flex justify-content-between mt-1">
            {due !== null ? (
              <small className="text-muted">
                Due: <span className="text-body">{formatCurrency(due)}</span>
              </small>
            ) : (
              <span />
            )}
            {total !== null ? (
              <small className="text-muted">
                Total: <span className="text-body">{formatCurrency(total)}</span>
              </small>
            ) : null}
          </div>
        ) : null}
      </div>
    </components.Option>
  );
}

export function InvoiceSingleValue(props: any) {
  const data = props.data as InvoiceSelectOption;

  const due =
    data.amountDue != null && Number.isFinite(data.amountDue)
      ? data.amountDue
      : data.outstandingAmount != null && Number.isFinite(data.outstandingAmount)
        ? data.outstandingAmount
        : null;

  return (
    <components.SingleValue {...props}>
      <span className="d-inline-flex align-items-center gap-1 min-w-0 text-truncate">
        <span className="text-truncate">{data.label}</span>
        {due !== null ? (
          <span className="text-muted text-truncate small">
            · Due {formatCurrency(due)}
          </span>
        ) : null}
      </span>
    </components.SingleValue>
  );
}
